import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";

import { Button } from "@/components/ui/button";
import { formatUyMinor } from "@/lib/formatUyMinor";
import type { QuoteRequestCharge } from "@/services/quoteRequestsService";

export interface QuoteChargeSummaryProps {
  /** Server-computed. This component never adds anything up itself. */
  charge: QuoteRequestCharge;
  /** i18n key for the total row's label — what the figure means right now. */
  totalLabelKey: string;
  /** Whether money is owed and the consumer may pay it. */
  canPay: boolean;
  /** Once paid and under way the amount stops leading; the work does. */
  isSettled?: boolean;
  /** Why there is no pay action, when there is a reason worth stating. */
  blockedNoteKey?: string | null;
  onPay: () => void;
  isPaying: boolean;
}

/**
 * What the consumer owes for an awarded RFQ, and the one action that pays it
 * (spec platform-fee-model §3d).
 *
 * One component for both shapes of request. The multi-discipline card and the
 * single-discipline page used to disagree about this: the card printed a total
 * and a Pagar button, the single-leg page printed a Pagar button with NO
 * amount anywhere near it. Adding a service fee to that would have meant a
 * consumer reading the bid in one card and being charged more from another.
 *
 * Subtotal and fee appear only when a fee applies. Printing "Subtotal" and
 * "Total" with nothing between them states one number twice and sends the
 * reader looking for a difference that is not there.
 */
export function QuoteChargeSummary({
  charge,
  totalLabelKey,
  canPay,
  isSettled = false,
  blockedNoteKey = null,
  onPay,
  isPaying,
}: QuoteChargeSummaryProps): JSX.Element {
  const { t } = useTranslation();
  const hasFee = charge.serviceFeeMinor > 0;
  const hasProfessionalIva = charge.professionalIvaMinor > 0;
  const hasDelivery = charge.deliveryFeeMinor > 0;
  // The breakdown appears when there is anything to break down. Printing
  // "Subtotal" and "Total" with nothing between them states one number twice.
  const hasBreakdown = hasFee || hasProfessionalIva || hasDelivery;

  return (
    /**
     * A PANEL WITH A NAME, not a grey strip. This block carries the figure the
     * consumer is about to be charged and the button that charges it, and it
     * was rendering as an unlabelled band of rows between the bids above and
     * the attachments below — the subtotal rows floating with no heading, the
     * invoice options nested inside as a second card, and "Total" marooned on
     * the far left of a wide screen from the amount on the far right.
     */
    <section className="rounded-lg border border-border bg-card p-4 shadow-sm sm:p-5">
      <h3 className="mb-3 text-sm font-semibold text-foreground">
        {t("requests.user.quoteChargeSummaryTitle")}
      </h3>
      {hasBreakdown ? (
        <dl className="mb-4 space-y-2 border-b border-border pb-4">
          <div className="flex items-baseline justify-between gap-4 text-sm">
            <dt className="text-muted-foreground">
              {t("requests.user.quoteLegsSubtotal")}
            </dt>
            <dd className="tabular-nums text-foreground">
              {formatUyMinor(charge.subtotalMinor, charge.currency)}
            </dd>
          </div>
          {hasProfessionalIva ? (
            <div className="flex items-baseline justify-between gap-4 text-sm">
              <dt className="text-muted-foreground">
                {t("requests.user.quoteReviewAwardSummaryProfessionalIva")}
              </dt>
              <dd className="tabular-nums text-foreground">
                {formatUyMinor(charge.professionalIvaMinor, charge.currency)}
              </dd>
            </div>
          ) : null}
          {hasFee ? (
            <div className="flex items-baseline justify-between gap-4 text-sm">
              {/* The same label the catalog wizard uses for the same thing, so
                  the platform charges ONE named fee rather than two unrelated
                  ones. */}
              <dt className="text-muted-foreground">
                {t("requests.user.pricingBreakdown.serviceFee")}
              </dt>
              <dd className="tabular-nums text-foreground">
                {formatUyMinor(charge.serviceFeeMinor, charge.currency)}
              </dd>
            </div>
          ) : null}
          {/* Its own line rather than folded into the fee: this one buys the
              consumer a physical thing arriving, and a total that grew by 270
              with no line to explain it reads as an error. */}
          {hasDelivery ? (
            <div className="flex items-baseline justify-between gap-4 text-sm">
              <dt className="text-muted-foreground">
                {t("requests.user.quoteDeliveryFee")}
              </dt>
              <dd className="tabular-nums text-foreground">
                {formatUyMinor(charge.deliveryFeeMinor, charge.currency)}
              </dd>
            </div>
          ) : null}
        </dl>
      ) : null}

      {/* THE FACTURA QUESTION IS ASKED AT THE TILL, not here.
          
          It used to sit in this panel, which meant the consumer answered it
          before choosing how to pay and then answered it AGAIN on the payment
          screen — where `CheckoutInvoiceFields` asks, and where the answer is
          stored on the checkout row. The server already treats the till answer
          as authoritative (fiscalo-invoice.service.ts prefers the checkout's
          `invoiceIncludeRut` over the request's copy), so the earlier of the
          two was not merely duplicated, it was overridden.

          Asking once, at the moment of payment, is also when the consumer
          actually knows whether this is a personal or a company purchase.

          WHERE THAT IS TRUE, precisely: /pay/mercadopago and
          /pay/bank-transfer. A dLocal checkout has no such screen — the
          consumer is redirected to dLocal's hosted page — so on dLocal this
          question is now asked NOWHERE and the preference stays "never
          asked". That is acceptable only because dLocal is being retired
          (task e833722); it must not reach production while PAYMENT_PROVIDER
          is still unset there. */}
      {/* The total sits WITH its amount and its button, as one group on the
          right, instead of the label drifting to the far edge of a wide screen
          from the figure it names. */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs text-muted-foreground">{t(totalLabelKey)}</p>
          <p
            className={
              isSettled
                ? "text-base font-medium tabular-nums text-muted-foreground"
                : "text-2xl font-semibold tracking-tight tabular-nums text-foreground"
            }
          >
            {formatUyMinor(charge.totalMinor, charge.currency)}
          </p>
        </div>
        {canPay ? (
          <Button type="button" size="lg" onClick={onPay} disabled={isPaying}>
            {t("requests.user.quoteReviewPayButton")}
          </Button>
        ) : null}
      </div>

      {/* Absent rather than disabled, with the reason stated: a greyed button
          with no explanation reads as broken. The server refuses the payment
          too — this is not the only guard. */}
      {blockedNoteKey != null ? (
        /* A WARNING, not a footnote. This was the smallest, faintest text on
           the one panel whose entire subject is money — and it answers the
           question a consumer looking for a missing Pagar button is actually
           asking. At `text-xs text-muted-foreground` it lost to every figure
           above it, so the screen read as "no way to pay, no reason given". */
        <Alert variant="warning" className="mt-3">
          <Info aria-hidden />
          <AlertDescription>{t(blockedNoteKey)}</AlertDescription>
        </Alert>
      ) : null}
    </section>
  );
}
